import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuggestionsModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    message: '',
    name: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.message.trim()) {
      toast.error('Please enter your message');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: formData.message,
          name: formData.name || null,
          email: formData.email || null
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Thank you for your feedback!');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit');
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ message: '', name: '', email: '' });
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
        onClick={resetForm}
        data-testid="suggestions-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          data-testid="suggestions-modal"
        >
          {/* Header */}
          <div className="p-6 border-b border-gold/10" style={{ background: '#1A1A1A' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(212, 149, 106, 0.2)' }}>
                  <MessageSquare size={20} style={{ color: '#d4956a' }} />
                </div>
                <div>
                  <h3 className="font-serif text-lg text-white">Share Your Thoughts</h3>
                  <p className="text-xs text-white/60">Private feedback for our team</p>
                </div>
              </div>
              <button
                onClick={resetForm}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                data-testid="suggestions-close-btn"
              >
                <X size={20} className="text-white/70" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#c07030' }}>
                  <CheckCircle className="text-white" size={32} />
                </div>
                <h4 className="font-serif text-xl mb-2" style={{ color: '#1A1A1A' }}>
                  Thank You!
                </h4>
                <p className="text-sm mb-6" style={{ color: '#1E3A5F' }}>
                  Your feedback has been received. We appreciate you taking the time to share your thoughts with us.
                </p>
                <button
                  onClick={resetForm}
                  className="px-6 py-2 rounded-full text-sm font-medium transition-colors"
                  style={{ background: '#f7ede0', color: '#c07030' }}
                >
                  Close
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Message */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Your Message *
                  </label>
                  <div className="relative">
                    <textarea
                      required
                      rows={4}
                      maxLength={2000}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 2000) })}
                      className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none text-sm"
                      placeholder="Share your suggestions, comments, or feedback..."
                      data-testid="suggestion-message-input"
                    />
                    <span className="absolute bottom-2 right-2 text-xs" style={{ color: '#9ca3af' }}>{formData.message.length}/2000</span>
                  </div>
                </div>

                {/* Name (Optional) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Name <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 100) })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 text-sm"
                    placeholder="Your name"
                    data-testid="suggestion-name-input"
                  />
                </div>

                {/* Email (Optional) */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                    Email <span className="text-xs font-normal" style={{ color: '#9ca3af' }}>(optional, for follow-up)</span>
                  </label>
                  <input
                    type="email"
                    maxLength={254}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value.slice(0, 254) })}
                    className="w-full px-4 py-3 rounded-xl border border-gold/20 focus:outline-none focus:ring-2 focus:ring-gold/50 text-sm"
                    placeholder="your@email.com"
                    data-testid="suggestion-email-input"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#c07030', color: 'white' }}
                  data-testid="suggestion-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Feedback
                    </>
                  )}
                </button>

                <p className="text-xs text-center mt-4" style={{ color: '#9ca3af' }}>
                  This message is private and only visible to ministry staff.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
